import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { useTranslation } from 'locales/logic';
import { keys, toSnakeCase } from 'remeda';
import { BooleanWithDefaultSchema } from 'types/boolean';
import { SendingMediaSchema } from 'types/sending-media';
import { z } from 'zod/v4';

import { SendingMediumSchema } from './use-load-company-configuration';
import {
  type ApiResponse,
  addActionNameToMutationResult,
  handleZodError,
  useLegacyAxiosInstance,
} from './utils';

const SendingMediumValidationSchema = z.array(
  SendingMediumSchema.extend({
    name: SendingMediaSchema.nullable(),
  }).optional(),
);

const StepsSwitchSchema = z.array(
  z.object({
    to: z.string(),
    from: z.array(z.string()),
  }),
);

export const UpdateClientPreferencesSchema = z.object({
  sendToAllEmails: BooleanWithDefaultSchema,
  sendToContactPersonAndDebtorEmail: BooleanWithDefaultSchema,
  allowPaymentPlans: BooleanWithDefaultSchema,
  recoveryPlanId: z.string().nullable(),
  defaultInvoiceTemplateId: z.string().nullable(),
  defaultCreditNoteTemplateId: z.string().nullable(),
  daysBetweenReminder: z.number(),
  minimumSaldoSending: z.coerce.number().nullable(),
  sendingMedium: SendingMediumValidationSchema,
  stepsSwitch: StepsSwitchSchema.optional(),
});

const UpdateClientPreferencesSnakeCase = UpdateClientPreferencesSchema.transform(data => {
  const transformedData: Record<string, unknown> = {};

  keys(data).forEach(key => {
    if (key === 'sendingMedium') {
      const filteredSendingMedium = data[key]
        .filter(medium => medium?.name && medium.name.trim() !== '')
        .map(medium => ({
          ...medium,
          force_sending: medium?.force_sending ? 'true' : 'false',
        }));
      transformedData.sending_media_attributes = filteredSendingMedium;
    } else if (
      key === 'sendToAllEmails' ||
      key === 'sendToContactPersonAndDebtorEmail' ||
      key === 'allowPaymentPlans'
    ) {
      // Convert 'default' to null as API doesn't understand 'default'
      transformedData[toSnakeCase(key)] = data[key] === 'default' ? null : data[key];
    } else {
      transformedData[toSnakeCase(key)] = data[key];
    }
  });

  delete transformedData.sending_medium;

  return transformedData;
});

export type UpdateClientPreferencesForm = z.infer<typeof UpdateClientPreferencesSchema>;
export type StepsSwitch = z.infer<typeof StepsSwitchSchema>;

export async function updateClientPreferencesFn(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: { id: string; data: UpdateClientPreferencesForm },
) {
  const instance = await axiosInstance;
  const safeParse = UpdateClientPreferencesSnakeCase.safeParse(data);
  if (!safeParse.success) {
    handleZodError(safeParse, 'client preferences');
  }

  return instance.put(`/debtors/${id}`, safeParse.data);
}

export function useUpdateClientPreferences() {
  const { t } = useTranslation();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientPreferencesForm }
  >({
    mutationFn: variables => updateClientPreferencesFn(axiosInstance, variables),
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientPreferencesForm },
    'updateClientPreferences'
  >('updateClientPreferences', mutation);
}
